import { Crown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { cn } from '../../lib/utils';
import type { LeaderboardEntry } from '../../types/leaderboard';

interface LeaderboardPodiumProps {
  /** Full ranking; only the top three are rendered on the podium. */
  entries: LeaderboardEntry[];
  /** Optional handler invoked when a podium climber is tapped (e.g. to open their breakdown). */
  onSelect?: (entry: LeaderboardEntry) => void;
}

// Per-rank visuals: gold / silver / bronze, with the winner standing taller and larger.
const RANK_STYLE: Record<number, { avatar: string; pedestal: string; number: string }> = {
  1: {
    avatar: 'h-16 w-16',
    pedestal: 'h-16 from-yellow-400/30 to-yellow-500/5 border-yellow-400/40',
    number: 'text-yellow-500',
  },
  2: {
    avatar: 'h-12 w-12',
    pedestal: 'h-11 from-slate-300/30 to-slate-400/5 border-slate-300/40',
    number: 'text-slate-400',
  },
  3: {
    avatar: 'h-12 w-12',
    pedestal: 'h-8 from-amber-600/30 to-amber-700/5 border-amber-600/40',
    number: 'text-amber-700',
  },
};

export default function LeaderboardPodium({ entries, onSelect }: LeaderboardPodiumProps) {
  const { t } = useTranslation();

  const top = entries.slice(0, 3);
  if (top.length === 0) return null;

  // Classic podium order: runner-up on the left, winner centred, third on the right.
  const podiumOrder = [top[1], top[0], top[2]].filter(Boolean) as LeaderboardEntry[];

  return (
    <div className="flex items-end justify-center gap-3 sm:gap-5" data-testid="leaderboard-podium">
      {podiumOrder.map(entry => {
        const style = RANK_STYLE[entry.rank] ?? RANK_STYLE[3];
        return (
          <button
            key={entry.user.id}
            type="button"
            onClick={onSelect ? () => onSelect(entry) : undefined}
            disabled={!onSelect}
            className="flex w-1/3 max-w-[120px] flex-col items-center transition-opacity enabled:hover:opacity-80"
            data-testid="podium-entry"
          >
            <div className="relative" data-testid="podium-avatar">
              {entry.rank === 1 && (
                <Crown className="absolute -top-4 left-1/2 h-5 w-5 -translate-x-1/2 fill-yellow-400 text-yellow-500" />
              )}
              <Avatar className={cn('border-2 border-background shadow-sm', style.avatar)}>
                <AvatarImage src={entry.user.avatar_small_url ?? undefined} alt={entry.user.username} />
                <AvatarFallback className="text-sm">{entry.user.username.charAt(0).toUpperCase()}</AvatarFallback>
              </Avatar>
            </div>

            <p className="mt-2 max-w-full truncate text-sm font-medium">{entry.user.username}</p>
            <p className="text-xs font-semibold tabular-nums text-muted-foreground">
              {Math.round(entry.total_points)} {t('leaderboard.pointsAbbr')}
            </p>

            <div
              className={cn(
                'mt-2 flex w-full items-start justify-center rounded-t-md border-x border-t bg-gradient-to-b pt-1.5',
                style.pedestal
              )}
            >
              <span className={cn('text-xl font-bold leading-none', style.number)}>{entry.rank}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
